import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, FilterIcon, PlusIcon, StoreIcon, ChevronDownIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { useRoleTheme } from '../../../hooks/useRoleTheme';
import BackButton from '../../../components/UI/BackButton';
import { bookstores, bookstoreBookLinks } from '../../../data/bookstoresData';
import { allBooks } from '../../../data/booksData';
const StockAvailability = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  // Mock vendor books (Elena Richards)
  const vendorBookIds = ['1', '2', '3'];
  const vendorBooks = allBooks.filter(b => vendorBookIds.includes(b.id));
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBooks, setSelectedBooks] = useState<string[]>([]);
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedBooks(vendorBooks.map(b => b.id));
    } else {
      setSelectedBooks([]);
    }
  };
  const handleSelectBook = (bookId: string) => {
    setSelectedBooks(prev => prev.includes(bookId) ? prev.filter(id => id !== bookId) : [...prev, bookId]);
  };
  const getAvailabilityBadge = (type: string) => {
    switch (type) {
      case 'stocked':
        return {
          label: 'In Stock',
          color: 'bg-green-100 text-green-800'
        };
      case 'online-listing':
        return {
          label: 'Online',
          color: 'bg-blue-100 text-blue-800'
        };
      case 'signed-copy':
        return {
          label: 'Signed',
          color: 'bg-purple-100 text-purple-800'
        };
      case 'pre-order':
        return {
          label: 'Pre-order',
          color: 'bg-amber-100 text-amber-800'
        };
      default:
        return {
          label: 'Available',
          color: 'bg-gray-100 text-gray-800'
        };
    }
  };
  return <div className={`min-h-screen py-8 ${dk.page}`} data-id="element-18943" data-mp="1wvnaj4xs2s45">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-18944" data-mp="ugxwgojutq3r">
        <div className="mb-6" data-id="element-18945" data-mp="zf7potm0qgmq">
          <BackButton data-id="element-18946" data-mp="zeg48bm4xza6" />
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4" data-id="element-18947" data-mp="zh28o2dqbyo6">
          <div data-id="element-18948" data-mp="18fxf9fkoras0">
            <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-18949" data-mp="18gfl93x5h3d4">
              Stock & Availability
            </h1>
            <p className={`text-sm mt-1 ${dk.textMuted}`} data-id="element-18950" data-mp="bqog8ar8fxt2">
              Manage where your books are available and their stock status.
            </p>
          </div>
          <Link to="/vendors/store-listings/link" className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${rt.primaryButton}`} data-id="element-18951" data-mp="kqlbwwc1hwxm">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-18952" data-mp="3o71sppuei6uo" />
            Add to Stores
          </Link>
        </div>

        {/* Filters & Bulk Actions */}
        <div className={`p-4 rounded-xl shadow-sm border mb-6 flex flex-col sm:flex-row gap-4 items-center justify-between ${dk.card}`} data-id="element-18953" data-mp="3t53fakr1coaw">
          <div className="flex items-center gap-4 w-full sm:w-auto" data-id="element-18954" data-mp="3gnj3h1ukdhdn">
            <div className="relative flex-1 sm:w-64" data-id="element-18955" data-mp="3goc6psubdor1">
              <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${dk.textMuted}`} data-id="element-18956" data-mp="3bnocyzepatt4" />
              <input type="text" placeholder="Search books..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full pl-9 pr-4 py-2 rounded-md text-sm focus:outline-none ${dk.inputOnCard} ${rt.inputFocus}`} data-id="element-18957" data-mp="37mkzuko1fbvl" />
            </div>
            <button className={`p-2 rounded-md border flex items-center justify-center ${dk.isDark ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'}`} data-id="element-18958" data-mp="2nzdhycvorocr">
              <FilterIcon className={`h-4 w-4 ${dk.textSecondary}`} data-id="element-18959" data-mp="2exx9wbscfybp" />
            </button>
          </div>

          {selectedBooks.length > 0 && <div className="flex items-center gap-2 w-full sm:w-auto" data-id="element-18960" data-mp="ix587vh8gwpa">
              <span className={`text-sm font-medium mr-2 ${dk.textPrimary}`} data-id="element-18961" data-mp="mvch645ggtpx">
                {selectedBooks.length} selected
              </span>
              <button className={`px-3 py-1.5 text-sm font-medium rounded border transition-colors ${dk.isDark ? 'border-gray-600 hover:bg-gray-700 text-gray-200' : 'border-gray-300 hover:bg-gray-50 text-gray-700'}`} data-id="element-18962" data-mp="e4c336hurmco">
                Update Status
              </button>
              <button className="px-3 py-1.5 text-sm font-medium rounded bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 transition-colors" data-id="element-18963" data-mp="3mbr95p36k4p4">
                Remove
              </button>
            </div>}
        </div>

        {/* Books List */}
        <div className="space-y-6" data-id="element-18964" data-mp="1kl626hia86mi">
          {vendorBooks.map(book => {
          const bookLinks = bookstoreBookLinks.filter(l => l.bookId === book.id);
          return <div key={book.id} className={`rounded-xl border overflow-hidden shadow-sm ${dk.card}`} data-id="element-18965" data-mp="1zl1ohhrfwhcp">
                {/* Book Header */}
                <div className={`p-4 border-b flex items-center gap-4 ${dk.isDark ? 'border-gray-700 bg-gray-800/80' : 'border-gray-200 bg-gray-50'}`} data-id="element-18966" data-mp="28hu70ggt0aeg">
                  <input type="checkbox" checked={selectedBooks.includes(book.id)} onChange={() => handleSelectBook(book.id)} className={`h-4 w-4 rounded border-gray-300 ${rt.checkbox}`} data-id="element-18967" data-mp="2ic2n24oneavq" />
                  <img src={book.coverImage} alt={book.title} className="w-10 h-14 object-cover rounded shadow-sm" data-id="element-18968" data-mp="2ndi83wqtshe4" />
                  <div className="flex-1" data-id="element-18969" data-mp="t0aiqklmcxzv">
                    <h3 className={`font-bold text-base ${dk.textPrimary}`} data-id="element-18970" data-mp="2s3qug2o6xoqr">
                      {book.title}
                    </h3>
                    <p className={`text-xs ${dk.textMuted}`} data-id="element-18971" data-mp="2x20tiod1d9hn">
                      {bookLinks.length} stores carrying this title
                    </p>
                  </div>
                  <Link to="/vendors/store-listings/link" className={`hidden sm:flex items-center px-3 py-1.5 text-sm font-medium rounded transition-colors ${rt.secondaryButton}`} data-id="element-18972" data-mp="370qckkgo4zzh">
                    <PlusIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-18973" data-mp="3g0da2v6a1ln9" />
                    Add to Store
                  </Link>
                </div>

                {/* Stores List for this book */}
                <div className="p-0" data-id="element-18974" data-mp="3pq6cb5kj4oeu">
                  {bookLinks.length > 0 ? <table className={`min-w-full divide-y ${dk.divider}`} data-id="element-18975" data-mp="3urdmtce241p7">
                      <thead className={dk.isDark ? 'bg-gray-800' : 'bg-white'} data-id="element-18976" data-mp="2mib9xmcgevs">
                        <tr data-id="element-18977" data-mp="2kfgz1e5rzl9">
                          <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-18978" data-mp="1n52njmo6ks7e">
                            Store
                          </th>
                          <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-18979" data-mp="1e4e8con1wlsx">
                            Availability
                          </th>
                          <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-18980" data-mp="21tgn9oqaw0jq">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${dk.divider} ${dk.isDark ? 'bg-gray-800/50' : 'bg-white'}`} data-id="element-18981" data-mp="2pjku04yscgey">
                        {bookLinks.map((link, idx) => {
                    const store = bookstores.find(s => s.id === link.bookstoreId);
                    if (!store) return null;
                    const badge = getAvailabilityBadge(link.availabilityType);
                    return <tr key={idx} data-id="element-18982" data-mp="2bv9a1ikh3vmj" data-mpi={idx}>
                              <td className="px-6 py-4 whitespace-nowrap" data-id="element-18983" data-mp="2bwajxjpr6c2o">
                                <div className="flex items-center" data-id="element-18984" data-mp="2gtauo7xjs304">
                                  <img src={store.logo} alt="" className="h-6 w-6 rounded-full object-cover mr-3" data-id="element-18985" data-mp="22vchv6yvkemq" />
                                  <span className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-18986" data-mp="rhqsplt0md17">
                                    {store.name}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap" data-id="element-18987" data-mp="xvwfrh96jmdm">
                                <div className="relative inline-block" data-id="element-18988" data-mp="xttn0pgn7f57">
                                  <select className={`appearance-none text-xs font-medium pl-3 pr-8 py-1 rounded-full border focus:outline-none focus:ring-2 focus:ring-offset-1 ${badge.color} ${dk.isDark ? 'border-transparent' : 'border-transparent'} ${rt.inputFocus}`} defaultValue={link.availabilityType} data-id="element-18989" data-mp="sxcpj4njzofg">
                                    <option value="stocked" data-id="element-18990" data-mp="a50pwpoab190">In Stock</option>
                                    <option value="signed-copy" data-id="element-18991" data-mp="3iku5916vq6oh">Signed</option>
                                    <option value="pre-order" data-id="element-18992" data-mp="3dnty0myamxvs">Pre-order</option>
                                    <option value="online-listing" data-id="element-18993" data-mp="1af66n7fwgmsm">
                                      Online
                                    </option>
                                  </select>
                                  <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 pointer-events-none" style={{
                            color: 'inherit'
                          }} data-id="element-18994" data-mp="10h07uype3wrz" />
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-18995" data-mp="hpzbmifnmfc4">
                                <button className="text-red-500 hover:text-red-700 transition-colors" data-id="element-18996" data-mp="csqqqa34fx76">
                                  Remove
                                </button>
                              </td>
                            </tr>;
                  })}
                      </tbody>
                    </table> : <div className="p-6 text-center" data-id="element-18997" data-mp="2jfeuuqzkfeff">
                      <p className={`text-sm ${dk.textMuted} mb-3`} data-id="element-18998" data-mp="2ji14qx9xi60t">
                        Not currently available in any stores.
                      </p>
                      <Link to="/vendors/store-listings/link" className={`inline-flex sm:hidden items-center px-3 py-1.5 text-sm font-medium rounded transition-colors ${rt.secondaryButton}`} data-id="element-18999" data-mp="2td2sbook301a">
                        <PlusIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-19000" data-mp="25ojl7ch8stas" />
                        Add to Store
                      </Link>
                    </div>}
                </div>
              </div>;
        })}
        </div>
      </div>
    </div>;
};
export default StockAvailability;